/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply.
   Matches the same exemption on table-internals/use-row-keyboard-navigation.ts. */
import type { RowData } from '@tanstack/react-table';
import * as React from 'react';

import { readOwnFlag } from '../table-internals/read-own-flag.js';
import type { ParentRow } from './DimeNestedTable.types.js';
import { readCachedChildren } from './read-cached-children.js';
import { shouldReleaseRequest } from './should-release-request.js';

interface ExpandAllRowsOptions<TParent extends RowData, TChild> {
  rows: ParentRow<TParent>[];
  readChildRows: () => Readonly<Record<string, TChild[] | undefined>>;
  readLoadingParentIds: () => Readonly<Record<string, boolean>> | undefined;
  isRequested: (parentId: string) => boolean;
  markRequested: (parentId: string) => number;
  clearRequested: (parentId: string, generation?: number) => void;
  hasConsumerReportedFetching: (parentId: string) => boolean;
  onExpandedChange: (
    updater: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  onExpandRow?: (parent: TParent, reportFailed: () => void) => void;
}

interface ExpandAllRows {
  expandAllRows: () => void;
  collapseAllRows: () => void;
}

/**
 * Bulk expansion over the parent rows currently on the page.
 *
 * Both operations issue exactly ONE onExpandedChange updater call. Expanding row-by-row would send
 * N single-key updates, and under a controlled `expanded` prop each would be computed from the same
 * pre-update state and clobber the previous one, leaving only the last row expanded.
 *
 * Expand-all can fan out one onExpandRow per uncached parent — that is N concurrent child fetches.
 */
export function useExpandAllRows<TParent extends RowData, TChild>({
  rows,
  readChildRows,
  readLoadingParentIds,
  isRequested,
  markRequested,
  clearRequested,
  hasConsumerReportedFetching,
  onExpandedChange,
  onExpandRow,
}: ExpandAllRowsOptions<TParent, TChild>): ExpandAllRows {
  const expandAllRows = React.useCallback(() => {
    // row.getCanExpand() is the single source of truth for the empty-row rule, so a parent cached
    // as [] is skipped here exactly as it is in the per-row toggle.
    const expandableRows = rows.filter((row) => row.getCanExpand());

    onExpandedChange((current) => {
      const next = { ...current };
      for (const row of expandableRows) next[row.id] = true;
      return next;
    });

    // getIsExpanded() still reflects pre-update state here, which is correct: a row that was
    // already expanded and is still uncached has a fetch in flight, so re-firing onExpandRow
    // would duplicate it. Keep this guard even though the header only offers Expand while
    // nothing is expanded — that gating is UI, not an invariant this hook can rely on.

    // Same snapshot source as the per-row toggle. Reading the props directly here let the two
    // expand paths reach opposite conclusions about whether a parent was loading, while both wrote
    // the same requested-id set.
    const currentChildRows = readChildRows();
    const currentLoadingParentIds = readLoadingParentIds();

    for (const row of expandableRows) {
      // A collapsed, uncached row whose fetch is still in flight would otherwise be re-fetched:
      // getIsExpanded() is false and the cache is undefined, so neither guard alone catches it.
      // Same release rule as the per-row toggle, via the shared predicate. Implementing only the
      // cached half here made a failed fetch permanently unretryable through the header button.
      if (
        shouldReleaseRequest({
          childRowsByParentId: currentChildRows,
          loadingParentIds: currentLoadingParentIds,
          parentId: row.id,
          hasConsumerReportedFetching,
        })
      ) {
        clearRequested(row.id);
      }
      if (
        !row.getIsExpanded() &&
        readCachedChildren(currentChildRows, row.id) === undefined &&
        !readOwnFlag(currentLoadingParentIds, row.id) &&
        !isRequested(row.id)
      ) {
        const generation = markRequested(row.id);
        onExpandRow?.(row.original as TParent, () => clearRequested(row.id, generation));
      }
    }
  }, [
    rows,
    readChildRows,
    readLoadingParentIds,
    isRequested,
    markRequested,
    clearRequested,
    hasConsumerReportedFetching,
    onExpandedChange,
    onExpandRow,
  ]);

  // Deletes only the keys for rows on the current page, mirroring expandAllRows's page scoping —
  // a controlled `expanded` prop can carry another page's already-expanded parent (that's the
  // documented way to persist expansion across a page change), and collapsing this page must not
  // discard it. TanStack reads expansion as `hasOwn(expanded, id) && expanded[id]`, so deleting a
  // key is equivalent to setting it false, without letting the record grow for every parent the
  // user ever opened on this page.
  const collapseAllRows = React.useCallback(() => {
    const currentPageRowIds = rows.map((row) => row.id);
    onExpandedChange((current) => {
      const next = { ...current };
      for (const rowId of currentPageRowIds) delete next[rowId];
      return next;
    });
  }, [rows, onExpandedChange]);

  return { expandAllRows, collapseAllRows };
}
